// @ts-strict-ignore
import { addExperimentGroupToTargeting } from '@platforms/shared';
import {
	context,
	defineExperiment,
	Experiment,
	getExperiment,
	InsertMethodType,
	InstantConfigService,
	utils,
} from '@wikia/ad-engine';
import { Injectable } from '@wikia/dependency-injection';
import { UcpMobileTopBoxadParagraphHelper } from './utils/ucp-mobile-top-boxad-paragraph-helper';

interface TopBoxadMultipleIcbExperimentConfig {
	anchorSelector: string;
	insertMethod: InsertMethodType;
}

type RunningExperimentType = Experiment | null | undefined;

const logGroup = 'UcpMobileTopBoxadMultipleIcbExperiment';

@Injectable()
export class UcpMobileTopBoxadMultipleIcbExperiment {
	private defaultAnchorSelector = context.get('templates.incontentAnchorSelector');
	private defaultNonHomeConfig: TopBoxadMultipleIcbExperimentConfig = {
		anchorSelector: this.defaultAnchorSelector,
		insertMethod: 'before',
	};
	private defaultHomeConfig: TopBoxadMultipleIcbExperimentConfig = {
		anchorSelector: '.mobile-main-page__wiki-description',
		insertMethod: 'after',
	};

	private experimentVariantNames = {
		control: 'top-boxad-multiple-icb-control',
		v1: 'top-boxad-multiple-icb-v1',
		v2: 'top-boxad-multiple-icb-v2',
	};

	/* TODO: Buckets still need to be assigned. */
	private experimentVariants = [
		defineExperiment({
			name: this.experimentVariantNames.control,
			buckets: ['3', '4', '5'],
		}),
		defineExperiment({
			name: this.experimentVariantNames.v1,
			buckets: ['6', '7', '8'],
		}),
		defineExperiment({
			name: this.experimentVariantNames.v2,
			buckets: ['9', '10', '11'],
		}),
	];

	private activeExperimentVariant: RunningExperimentType = getExperiment(this.experimentVariants);

	constructor(
		private instantConfig: InstantConfigService,
		private paragraphHelper: UcpMobileTopBoxadParagraphHelper,
	) {}

	public getConfig(): TopBoxadMultipleIcbExperimentConfig {
		if (this.isExperimentEnabled()) {
			utils.logger(logGroup, 'New top_boxad config');
			return this.getExperimentConfig();
		}

		utils.logger(logGroup, this.isHome() ? 'Home page' : 'Default config');
		return this.isHome() ? this.defaultHomeConfig : this.defaultNonHomeConfig;
	}

	private getExperimentConfig(): TopBoxadMultipleIcbExperimentConfig {
		if (this.isExperimentEnabled() && this.activeExperimentVariant?.name) {
			let anchorSelector: string;
			switch (this.activeExperimentVariant.name) {
				case this.experimentVariantNames.v1:
					utils.logger(logGroup, 'V1 group');
					context.set('experiments.mobileTopBoxadMultipleIcb', true);
					context.set('experiments.mobileTopBoxadMultipleIcbCount', 800);
					addExperimentGroupToTargeting(this.experimentVariantNames.v1);
					anchorSelector = this.getAnchorSelectorByCharacterLimit(800);
					if (anchorSelector) {
						return this.prepareConfig(anchorSelector, 'after');
					}
					return this.defaultNonHomeConfig;
				case this.experimentVariantNames.v2:
					utils.logger(logGroup, 'V2 group');
					context.set('experiments.mobileTopBoxadMultipleIcb', true);
					context.set('experiments.mobileTopBoxadMultipleIcbCount', 1200);
					addExperimentGroupToTargeting(this.experimentVariantNames.v2);
					anchorSelector = this.getAnchorSelectorByCharacterLimit(1200);
					if (anchorSelector) {
						return this.prepareConfig(anchorSelector, 'after');
					}
					return this.defaultNonHomeConfig;
				case this.experimentVariantNames.control:
					utils.logger(logGroup, 'Control group');
					context.set('experiments.mobileTopBoxadMultipleIcb', false);
					addExperimentGroupToTargeting(this.experimentVariantNames.control);
					return this.defaultNonHomeConfig;
			}

			utils.logger(
				logGroup,
				'experiments.mobileTopBoxadMultipleIcb',
				context.get('experiments.mobileTopBoxadMultipleIcb'),
			);
		} else {
			utils.logger(logGroup, 'Experiment disabled');
			context.set('experiments.mobileTopBoxadMultipleIcb', false);
			return;
		}
	}

	private getAnchorSelectorByCharacterLimit(characterLimit: number): string | null {
		const paragraphs = Array.from(
			document.querySelectorAll(this.paragraphHelper.getParagraphSelector()),
		);
		let characterCount = 0;

		for (const paragraph of paragraphs) {
			characterCount += paragraph.textContent?.length ?? 0;

			if (characterCount >= characterLimit) {
				const paragraphData = this.paragraphHelper.getParagraphData(
					paragraphs,
					(_, index) => index >= paragraphs.indexOf(paragraph),
				);

				if (
					this.paragraphHelper.existParagraph(paragraphData) &&
					this.isParagraphBreak(paragraphData.element)
				) {
					if (!this.isInSameViewportAsNextAd(paragraphData.element)) {
						return this.paragraphHelper.getNthParagraphSelector(paragraphData.nthOfType);
					}
				}
			}
		}

		return null;
	}

	private isParagraphBreak(paragraph: Element): boolean {
		return !!paragraph.nextElementSibling;
	}

	private isInSameViewportAsNextAd(paragraph: Element): boolean {
		const nextAdSelector = this.defaultAnchorSelector;
		const nextAdElement = document.querySelector(nextAdSelector);

		if (nextAdElement) {
			const paragraphRect = paragraph.getBoundingClientRect();
			const nextAdRect = nextAdElement.getBoundingClientRect();

			return paragraphRect.bottom >= nextAdRect.top && paragraphRect.top <= nextAdRect.bottom;
		}

		return false;
	}

	private prepareConfig(
		anchorSelector: string,
		insertMethod: InsertMethodType,
	): TopBoxadMultipleIcbExperimentConfig {
		return {
			anchorSelector: anchorSelector,
			insertMethod: insertMethod,
		};
	}

	private isHome() {
		return context.get('wiki.targeting.pageType') === 'home';
	}

	private isExperimentEnabled() {
		const isIcbMobileTopBoxadMultipleIcb = this.instantConfig
			.get('icExperiments', [])
			.includes('mobileTopBoxadMultipleIcb');
		const isExperimentVariant = this.activeExperimentVariant;

		utils.logger(logGroup, 'isIcbMobileTopBoxadMultipleIcb: ', isIcbMobileTopBoxadMultipleIcb);
		utils.logger(logGroup, 'isExperimentVariant: ', isExperimentVariant);

		return isIcbMobileTopBoxadMultipleIcb && isExperimentVariant;
	}
}
